/**
 * CP-127: filter empty-results recovery for Browse and Hot Deals.
 *
 * When active filters return nothing, the grid shows a short recovery line,
 * a Clear all filters action, and one "Remove …" action per active filter
 * instead of a bare "Showing 0 of 0". Each option carries the patch that
 * drops just that filter, so the page can relax it without a reload.
 *
 * Order is a fixed restrictiveness heuristic (no extra count requests):
 * price caps and single-ship / single-port picks usually cut the most, broad
 * line / destination picks the least. Sort is never a filter here (CP-121).
 *
 * Plain functions only (no imports) so the node test can load this file.
 */

function present(value) {
  if (value === null || value === undefined) return false;
  return String(value).trim() !== '';
}

function priceText(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function lookup(labels, key, value) {
  const fn = labels && typeof labels[key] === 'function' ? labels[key] : null;
  const label = fn ? fn(value) : '';
  return present(label) ? String(label).trim() : String(value).trim();
}

const BROWSE_DURATION_LABELS = {
  '1-5': '1–5 nights',
  '6-9': '6–9 nights',
  '10-14': '10–14 nights',
  '15+': '15+ nights'
};

function dateRangeLabel(start, end) {
  if (start && end) return `${start} to ${end}`;
  if (start) return `From ${start}`;
  return `Until ${end}`;
}

/**
 * Browse (browseQuery state shape) → [{ key, label, patch }].
 * `patch` is a mergeBrowseQuery patch. `labels.destination(id)` and
 * `labels.departure_port(key)` map stored values onto consumer labels.
 */
export function browseRelaxOptions(state, labels) {
  const s = state && typeof state === 'object' ? state : {};
  const out = [];
  if (present(s.max_price)) {
    out.push({ key: 'max_price', label: `Max $${priceText(s.max_price)}`, patch: { max_price: null } });
  }
  if (present(s.min_price)) {
    out.push({ key: 'min_price', label: `Min $${priceText(s.min_price)}`, patch: { min_price: null } });
  }
  if (present(s.ship_name)) {
    out.push({ key: 'ship_name', label: String(s.ship_name).trim(), patch: { ship_name: '' } });
  }
  if (present(s.cabin)) {
    // CP-355: Browse only. A cabin plus a price cap is the usual empty result.
    out.push({ key: 'cabin', label: `${String(s.cabin).trim()} cabin`, patch: { cabin: '' } });
  }
  if (present(s.departure_port)) {
    out.push({
      key: 'departure_port',
      label: `From ${lookup(labels, 'departure_port', s.departure_port)}`,
      patch: { departure_port: '' }
    });
  }
  if (present(s.start_date) || present(s.end_date)) {
    // One action for the range: Home's month sets both ends together.
    out.push({
      key: 'dates',
      label: dateRangeLabel(present(s.start_date) ? s.start_date : '', present(s.end_date) ? s.end_date : ''),
      patch: { start_date: '', end_date: '' }
    });
  }
  if (present(s.duration)) {
    out.push({
      key: 'duration',
      label: BROWSE_DURATION_LABELS[s.duration] || String(s.duration),
      patch: { duration: '' }
    });
  }
  if (present(s.cruise_line)) {
    out.push({ key: 'cruise_line', label: String(s.cruise_line).trim(), patch: { cruise_line: '' } });
  }
  if (present(s.destination)) {
    out.push({
      key: 'destination',
      label: lookup(labels, 'destination', s.destination),
      patch: { destination: '' }
    });
  }
  return out;
}

function nightsRangeLabel(min, max) {
  if (present(min) && present(max)) {
    return String(min) === String(max) ? `${min} nights` : `${min}–${max} nights`;
  }
  if (present(min)) return `${min}+ nights`;
  return `Up to ${max} nights`;
}

/**
 * Hot Deals (DealFilters emitted filters) → [{ key, label, patch }].
 * `patch` lists the DealFilters keys to blank out.
 */
export function dealRelaxOptions(filters, labels) {
  const f = filters && typeof filters === 'object' ? filters : {};
  const out = [];
  if (present(f.max_price)) {
    out.push({ key: 'max_price', label: `Max $${priceText(f.max_price)}`, patch: { max_price: null } });
  }
  if (present(f.min_price)) {
    out.push({ key: 'min_price', label: `Min $${priceText(f.min_price)}`, patch: { min_price: null } });
  }
  if (present(f.ship_name)) {
    out.push({ key: 'ship_name', label: String(f.ship_name).trim(), patch: { ship_name: '' } });
  }
  if (present(f.departure_port)) {
    out.push({
      key: 'departure_port',
      label: `From ${lookup(labels, 'departure_port', f.departure_port)}`,
      patch: { departure_port: '' }
    });
  }
  if (present(f.min_duration) || present(f.max_duration)) {
    out.push({
      key: 'duration',
      label: nightsRangeLabel(f.min_duration, f.max_duration),
      patch: { min_duration: null, max_duration: null }
    });
  }
  if (present(f.cruise_line)) {
    out.push({ key: 'cruise_line', label: String(f.cruise_line).trim(), patch: { cruise_line: '' } });
  }
  if (present(f.destination)) {
    out.push({
      key: 'destination',
      label: lookup(labels, 'destination', f.destination),
      patch: { destination: '' }
    });
  }
  return out;
}

/** Short recovery line. Plain wording only, no marketing copy. */
export const EMPTY_RECOVERY_LINE = {
  cruises: 'No cruises match these filters.',
  deals: 'No deals match these filters.'
};
